import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Newspaper, Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const empty = { outlet: "", title: "", url: "", date: "", blurb: "", featured: false };

export default function MediaMentions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/media-mentions")).data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this mention?")) return;
    await api.delete(`/media-mentions/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto space-y-6" data-testid="media-mentions-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Newspaper className="w-3 h-3" /> Media &amp; press</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">Media mentions</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-2xl">Every entry here shows up on the public /press page under "In the news". Featured mentions are pinned to the top.</p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="gap-2" data-testid="mention-new-btn">
          <Plus className="w-4 h-4" /> Add mention
        </Button>
      </header>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center">
          <Newspaper className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm mb-1">No media mentions yet.</p>
          <p className="text-xs text-muted-foreground mb-4">Add outlet, title, URL and a short blurb as coverage arrives.</p>
          <Button onClick={() => setDialogOpen(true)} className="gap-2"><Plus className="w-4 h-4" /> Add your first</Button>
        </div>
      ) : (
        <div className="glass-card rounded-2xl divide-y divide-white/5" data-testid="mentions-list">
          {items.map((m) => (
            <div key={m.id} className="p-4 flex items-center gap-4" data-testid={`mention-${m.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs label-eyebrow gold flex items-center gap-1">
                  {m.outlet} • {m.date}
                  {m.featured && <Star className="w-3 h-3 fill-primary text-primary" />}
                </div>
                <div className="text-sm mt-1 font-medium truncate">{m.title}</div>
                {m.blurb && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{m.blurb}</div>}
              </div>
              <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-xs gold hover:underline inline-flex items-center gap-1 shrink-0"><ExternalLink className="w-3 h-3" /> Link</a>
              <Button size="sm" variant="outline" onClick={() => { setEditing(m); setDialogOpen(true); }} className="h-7 gap-1 text-xs" data-testid={`mention-edit-${m.id}`}><Pencil className="w-3 h-3" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(m.id)} className="h-7" data-testid={`mention-delete-${m.id}`}><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
        </div>
      )}

      <MentionDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={load} />
    </div>
  );
}

function MentionDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => { if (open) setForm(initial ? { ...empty, ...initial } : empty); }, [open, initial]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.outlet.trim() || !form.title.trim() || !form.url.trim() || !form.date.trim()) {
      toast.error("Outlet, title, URL, and date are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/media-mentions/${initial.id}`, form);
      else await api.post("/media-mentions", form);
      toast.success(isEdit ? "Updated" : "Mention added");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="mention-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isEdit ? "Edit mention" : "New mention"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Outlet</Label><Input value={form.outlet} onChange={(e) => set("outlet", e.target.value)} placeholder="The Chef's Table" data-testid="mention-outlet" /></div>
          <div><Label>Article title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="The costing tool every kitchen needed" data-testid="mention-title" /></div>
          <div><Label>URL</Label><Input value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" data-testid="mention-url" /></div>
          <div><Label>Date</Label><Input value={form.date} onChange={(e) => set("date", e.target.value)} placeholder="Mar 2026" data-testid="mention-date" /></div>
          <div><Label>Blurb (optional)</Label><Textarea rows={3} value={form.blurb || ""} onChange={(e) => set("blurb", e.target.value)} placeholder="One or two sentences." data-testid="mention-blurb" /></div>
          <div className="flex items-center justify-between border border-white/10 rounded p-3">
            <div><div className="text-sm font-medium">Featured</div><div className="text-xs text-muted-foreground">Pin to the top of /press.</div></div>
            <Switch checked={!!form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="mention-featured" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="mention-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
